import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { getMe, isApiError, logout as apiLogout, setSessionExpiredHandler } from '../services/api';

/**
 * Utilisateur connecté tel que vu par le frontend (payload de GET /api/auth/me).
 * `isTeacher` est un alias rétro-compatible de `role === 'admin'`.
 */
export interface AppUser {
  id: string;
  pseudonym: string;
  email?: string;
  role: 'student' | 'admin';
  createdAt: string;
  lastActive: string;
  emailVerifiedAt?: string;
  /** null tant que le questionnaire de niveau n'a pas été validé ou passé (l'accueil l'affiche alors). */
  onboardingCompletedAt: string | null;
  isTeacher: boolean;
}

export interface UserContextValue {
  user: AppUser | null;
  isLoading: boolean;
  logout: () => Promise<void>;
  refreshUser: () => void;
  /** Message à afficher sur la page de connexion après une expiration de session. */
  sessionExpired: string | null;
  /** Efface ce message (appelé par la page de connexion une fois affiché). */
  clearSessionExpired: () => void;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

/** Clé de passage du message « session expirée » jusqu'à la page de connexion. */
export const SESSION_EXPIRED_KEY = 'prog2d.sessionExpired';

/**
 * Provider unique de l'utilisateur connecté.
 *
 * Avant : `useUser()` était un hook à état LOCAL appelé dans ~20 composants → chaque
 * composant refaisait son propre `GET /api/auth/me` au montage (18 appels mesurés sur
 * un chargement d'accueil en admin) et possédait son propre objet `user`, ce qui
 * relançait aussi les effets dépendant de `[user]` (QuestSection, suggestions…).
 *
 * Maintenant : UN seul fetch pour toute l'app, partagé par contexte. La signature de
 * retour de `useUser()` est inchangée (`{ user, isLoading, logout, refreshUser }`).
 */
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState<string | null>(() => {
    // Un rechargement complet peut avoir eu lieu entre le 401 et l'affichage.
    try {
      return window.sessionStorage.getItem(SESSION_EXPIRED_KEY);
    } catch {
      return null;
    }
  });
  // Dédoublonne les fetchs concurrents (StrictMode double-monte les effets en dev,
  // et plusieurs `refreshUser()` peuvent être appelés dans la même frame).
  const inflight = useRef<Promise<void> | null>(null);

  const fetchUser = useCallback((): Promise<void> => {
    if (inflight.current) return inflight.current;

    const run = (async () => {
      try {
        const me = await getMe();
        setUser({
          id: me.id,
          pseudonym: me.pseudonym,
          email: me.email,
          role: me.role,
          createdAt: me.createdAt,
          lastActive: me.lastActive,
          emailVerifiedAt: me.emailVerifiedAt,
          onboardingCompletedAt: me.onboardingCompletedAt ?? null,
          isTeacher: me.role === 'admin', // Backward compatibility
        });
      } catch (error) {
        // 401 (personne n'est connecté) ou autre refus du serveur : pas d'utilisateur, en silence.
        // Seule une erreur réseau mérite une trace en console.
        if (!isApiError(error) || error.isNetworkError) {
          console.error("Erreur lors de la récupération de l'utilisateur:", error);
        }
        setUser(null);
      } finally {
        setIsLoading(false);
        inflight.current = null;
      }
    })();

    inflight.current = run;
    return run;
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /**
   * Session expirée. Quand le JWT expire pendant la session, chaque page affichait autrefois
   * son propre « Erreur 401 » et l'utilisateur restait sur une interface qui semblait marcher
   * jusqu'au prochain rechargement. Le client API (`services/api/client.ts`) appelle ce
   * gestionnaire pour tout 401 sur une route `/api/…` hors `/api/auth/*` (login, identité
   * initiale, invitation… gèrent leur propre 401) : on vide l'utilisateur et on renvoie
   * sur /login avec un message. Pas de verrou « une seule fois » : une deuxième expiration
   * dans le même onglet doit être interceptée aussi ; plusieurs 401 simultanés appellent
   * `setUser(null)` plusieurs fois, sans effet. `setUser(null)` suffit à renvoyer sur /login
   * (toutes les routes applicatives de App.tsx sont gardées par `user ? … : <Navigate to="/login" />`) ;
   * on évite `window.location.href`, dont le rechargement complet effaçait le message.
   */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (window.location.pathname === '/login') return;
      setSessionExpired('Session expirée, reconnectez-vous.');
      setUser(null);
      try {
        window.sessionStorage.setItem(SESSION_EXPIRED_KEY, 'Session expirée, reconnectez-vous.');
      } catch {
        /* sessionStorage indisponible : le message passe par le contexte */
      }
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      setUser(null);
      window.location.href = '/login';
    }
  }, []);

  // Le chargement plein écran d'App.tsx démonte toutes les pages : on ne l'affiche que
  // quand personne n'est encore chargé (arrivée d'une invitation). Un utilisateur déjà
  // connecté (pseudonyme modifié, questionnaire validé) est mis à jour sur place, sans
  // perdre l'état de la page qui a demandé le rafraîchissement.
  const userRef = useRef<AppUser | null>(user);
  userRef.current = user;

  const refreshUser = useCallback(() => {
    if (!userRef.current) setIsLoading(true);
    fetchUser();
  }, [fetchUser]);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(null);
    try {
      window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    } catch {
      /* rien à faire */
    }
  }, []);

  const value = useMemo<UserContextValue>(
    () => ({ user, isLoading, logout, refreshUser, sessionExpired, clearSessionExpired }),
    [user, isLoading, logout, refreshUser, sessionExpired, clearSessionExpired]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

/**
 * Hook d'accès à l'utilisateur connecté. Même signature qu'avant le passage au contexte.
 * Doit être appelé sous `<UserProvider>` (monté dans `main.tsx`).
 */
export function useUser(): UserContextValue {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser doit être utilisé à l'intérieur d'un UserProvider");
  }
  return context;
}

export { UserContext };
